import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/Button";
import { openWhatsApp } from "../utils/whatsapp";
import { SEO } from "../components/SEO";

export default function StoriesPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Insights & Stories"
        description="Discover valuable tips, success stories, and expert advice on studying abroad, visa applications, and international education. Expert insights from BeginPack Ltd."
        ogTitle="Expert Insights & Stories - BeginPack Ltd"
        ogDescription="Get expert tips, success stories, and guidance on studying abroad, visa applications, and international education. Your resource for global education opportunities."
      />
      {/* Hero Section */}
      <div className="relative bg-cover bg-center bg-no-repeat py-20 bg-gradient-to-br from-[#004E89] to-[#800020] md:py-28 lg:py-32">
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-poppins text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight antialiased mb-4 text-white">
            Expert Insights & Guides
          </h1>
          <p className="font-jakarta text-lg md:text-xl lg:text-2xl text-white">
            Discover valuable tips, success stories, and expert advice to guide
            your international education journey
          </p>
        </div>
      </div>

      {/* Study Abroad Blog Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {/* Blog 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-2xl p-8 md:p-12"
            >
              <h3 className="font-poppins text-2xl md:text-3xl font-bold text-gray-900 mb-6 tracking-tight antialiased">
                Explore Global Education Opportunities: Your Guide to Studying
                Abroad
              </h3>
              <div className="space-y-6 text-gray-700">
                <p className="font-jakarta leading-relaxed tracking-tight antialiased">
                  Studying abroad is more than just earning a degree it's a
                  life-changing experience that opens doors to new cultures,
                  networks, and career opportunities. Whether you're planning to
                  study in Europe, the UK, Canada, or other international
                  destinations, making the right choices can shape your future.
                </p>

                <div>
                  <h4 className="font-poppins text-xl font-bold text-gray-900 mb-3 tracking-tight antialiased">
                    Choosing the Right Course and University
                  </h4>
                  <p className="font-jakarta leading-relaxed tracking-tight antialiased">
                    Selecting the ideal program and university is the first step
                    toward a successful study abroad journey. Consider your
                    academic interests, career goals, and lifestyle preferences.
                    Research university rankings, course content, tuition fees,
                    work opportunities, and scholarship options to find the best
                    fit for you.
                  </p>
                </div>

                <div>
                  <h4 className="font-poppins text-xl font-bold text-gray-900 mb-3 tracking-tight antialiased">
                    Application and Scholarship Guidance
                  </h4>
                  <p className="font-jakarta leading-relaxed tracking-tight antialiased">
                    The application process can be complex, but with the right
                    guidance, it becomes manageable. From preparing and
                    submitting applications to applying for scholarships, having
                    expert support increases your chances of success while
                    saving you time and effort.
                  </p>
                </div>

                <div>
                  <h4 className="font-poppins text-xl font-bold text-gray-900 mb-3 tracking-tight antialiased">
                    Visa and Pre-Departure Preparation
                  </h4>
                  <p className="font-jakarta leading-relaxed tracking-tight antialiased">
                    Navigating visa applications can be tricky. Understanding
                    eligibility, filing requirements, and compliance is crucial.
                    Pre-departure briefings equip you with essential information
                    on travel, culture, academics, and daily life abroad,
                    helping you adjust smoothly to your new environment.
                  </p>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="font-poppins text-xl font-bold text-gray-900 mb-3 tracking-tight antialiased">
                    Why Choose BeginPack for Your Study Abroad Journey?
                  </h4>
                  <p className="font-jakarta leading-relaxed tracking-tight antialiased">
                    With so many options and processes to navigate, professional
                    guidance ensures you make informed decisions, avoid common
                    pitfalls, and fully enjoy the opportunities that come with
                    studying abroad. From selecting the right course and
                    university to securing visas, scholarships, and
                    accommodation, our expert support simplifies the journey and
                    maximizes your success. Start planning today and take the
                    first step toward an exciting global education experience.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Blog 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gray-50 rounded-2xl p-8 md:p-12"
            >
              <h3 className="font-poppins text-2xl md:text-3xl font-bold text-gray-900 mb-6 tracking-tight antialiased">
                Top Study Destinations for Nigerians: Explore Your Global
                Education Options
              </h3>
              <div className="space-y-6 text-gray-700">
                <p className="font-jakarta leading-relaxed tracking-tight antialiased">
                  Studying abroad is a dream for many Nigerian students,
                  offering access to world-class education, global exposure, and
                  better career opportunities. Choosing the right destination is
                  crucial for academic success, personal growth, and future
                  prospects. Here's a guide to some of the top study
                  destinations for Nigerians.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* UK */}
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h4 className="font-poppins text-lg font-bold text-[#800020] mb-3 tracking-tight antialiased">
                      1. United Kingdom (UK)
                    </h4>
                    <p className="font-jakarta text-sm text-gray-600 mb-2 tracking-tight antialiased">
                      The UK is one of the most popular destinations for
                      Nigerian students due to its globally recognized
                      universities and diverse programs.
                    </p>
                    <div className="space-y-1 text-sm">
                      <p>
                        <strong>Pros:</strong> Shorter course durations, quality
                        education, post-study work opportunities, and cultural
                        diversity.
                      </p>
                      <p>
                        <strong>Popular Courses:</strong> Engineering, Medicine,
                        Business, Law, and Computer Science.
                      </p>
                      <p>
                        <strong>Top Universities:</strong> University of Oxford,
                        University of Cambridge, Imperial College London,
                        University College London (UCL).
                      </p>
                    </div>
                  </div>

                  {/* Canada */}
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h4 className="font-poppins text-lg font-bold text-[#800020] mb-3 tracking-tight antialiased">
                      2. Canada
                    </h4>
                    <p className="font-jakarta text-sm text-gray-600 mb-2 tracking-tight antialiased">
                      Canada offers high-quality education with a focus on
                      research, innovation, and multicultural living. Nigerian
                      students are drawn to its welcoming environment and
                      opportunities for permanent residency.
                    </p>
                    <div className="space-y-1 text-sm">
                      <p>
                        <strong>Pros:</strong> Affordable tuition compared to
                        the US and UK, work while studying, strong
                        post-graduation work permits, and safe living
                        conditions.
                      </p>
                      <p>
                        <strong>Popular Courses:</strong> IT, Business, Health
                        Sciences, and Engineering.
                      </p>
                      <p>
                        <strong>Top Universities:</strong> University of
                        Toronto, University of British Columbia, McGill
                        University, University of Waterloo.
                      </p>
                    </div>
                  </div>

                  {/* USA */}
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h4 className="font-poppins text-lg font-bold text-[#800020] mb-3 tracking-tight antialiased">
                      3. United States (USA)
                    </h4>
                    <p className="font-jakarta text-sm text-gray-600 mb-2 tracking-tight antialiased">
                      The USA is known for its prestigious universities and
                      flexible education system. It attracts Nigerian students
                      seeking global recognition and career opportunities.
                    </p>
                    <div className="space-y-1 text-sm">
                      <p>
                        <strong>Pros:</strong> Wide range of programs, research
                        opportunities, cultural diversity, and extensive
                        scholarships.
                      </p>
                      <p>
                        <strong>Popular Courses:</strong> STEM fields, Business,
                        Medicine, Arts, and Social Sciences.
                      </p>
                      <p>
                        <strong>Top Universities:</strong> Harvard University,
                        Massachusetts Institute of Technology (MIT), Stanford
                        University, University of California, Berkeley.
                      </p>
                    </div>
                  </div>

                  {/* Australia */}
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h4 className="font-poppins text-lg font-bold text-[#800020] mb-3 tracking-tight antialiased">
                      4. Australia
                    </h4>
                    <p className="font-jakarta text-sm text-gray-600 mb-2 tracking-tight antialiased">
                      Australia is known for its innovative universities and
                      multicultural environment. It is popular among Nigerian
                      students seeking opportunities in Asia-Pacific.
                    </p>
                    <div className="space-y-1 text-sm">
                      <p>
                        <strong>Pros:</strong> High-quality education,
                        post-study work visas, research opportunities, and
                        student-friendly cities.
                      </p>
                      <p>
                        <strong>Popular Courses:</strong> Business, IT,
                        Engineering, Health Sciences, and Hospitality.
                      </p>
                      <p>
                        <strong>Top Universities:</strong> University of
                        Melbourne, University of Sydney, Australian National
                        University, University of Queensland.
                      </p>
                    </div>
                  </div>

                  {/* Germany */}
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h4 className="font-poppins text-lg font-bold text-[#800020] mb-3 tracking-tight antialiased">
                      5. Germany
                    </h4>
                    <p className="font-jakarta text-sm text-gray-600 mb-2 tracking-tight antialiased">
                      Germany is ideal for students looking for quality
                      education at low or no tuition fees. Its technical
                      universities are highly regarded worldwide.
                    </p>
                    <div className="space-y-1 text-sm">
                      <p>
                        <strong>Pros:</strong> Tuition-free or low-cost
                        programs, strong engineering and technical courses, and
                        post-study work options.
                      </p>
                      <p>
                        <strong>Popular Courses:</strong> Engineering, Computer
                        Science, Medicine, and Business.
                      </p>
                      <p>
                        <strong>Top Universities:</strong> Technical University
                        of Munich, Ludwig Maximilian University of Munich,
                        University of Heidelberg.
                      </p>
                    </div>
                  </div>

                  {/* Netherlands */}
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h4 className="font-poppins text-lg font-bold text-[#800020] mb-3 tracking-tight antialiased">
                      6. Netherlands
                    </h4>
                    <p className="font-jakarta text-sm text-gray-600 mb-2 tracking-tight antialiased">
                      The Netherlands offers programs in English and a strong
                      focus on innovation, research, and international exposure.
                    </p>
                    <div className="space-y-1 text-sm">
                      <p>
                        <strong>Pros:</strong> Affordable tuition,
                        English-taught programs, flexible study options, and
                        easy travel across Europe (Schengen area).
                      </p>
                      <p>
                        <strong>Popular Courses:</strong> Business, Engineering,
                        Arts, and Social Sciences.
                      </p>
                      <p>
                        <strong>Top Universities:</strong> University of
                        Amsterdam, Delft University of Technology, Leiden
                        University, Utrecht University.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="font-poppins text-xl font-bold text-gray-900 mb-4 tracking-tight antialiased">
                    Tips for Choosing the Right Study Destination
                  </h4>
                  <ul className="space-y-3 font-jakarta leading-relaxed tracking-tight antialiased list-disc list-inside">
                    <li>
                      <strong>Consider Your Budget:</strong> Tuition fees,
                      living costs, and scholarships vary across countries.
                    </li>
                    <li>
                      <strong>Check Visa & Work Opportunities:</strong> Some
                      countries allow students to work while studying or offer
                      post-study work visas.
                    </li>
                    <li>
                      <strong>Language of Instruction:</strong> Make sure you
                      are comfortable with the primary language used in classes.
                    </li>
                    <li>
                      <strong>Career Goals:</strong> Choose destinations with
                      strong opportunities in your field of interest.
                    </li>
                    <li>
                      <strong>Lifestyle & Culture:</strong> Consider safety,
                      climate, and cultural fit for a smooth transition.
                    </li>
                  </ul>

                  <div className="mt-6">
                    <h4 className="font-poppins text-xl font-bold text-gray-900 mb-3 tracking-tight antialiased">
                      Conclusion
                    </h4>
                    <p className="font-jakarta leading-relaxed tracking-tight antialiased">
                      Nigerian students today have many exciting options for
                      studying abroad. The UK, Canada, USA, Germany, Australia,
                      and the Netherlands each offer unique benefits and
                      opportunities. By carefully considering your goals,
                      budget, and lifestyle, you can choose the destination that
                      aligns perfectly with your academic and career
                      aspirations. Start planning today, explore scholarship
                      opportunities, and take the first step toward an enriching
                      international education experience!
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#004E89] to-[#800020]">
        <div className="max-w-4xl mx-auto text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-poppins text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight antialiased">
              Your Journey Starts Here
            </h2>
            <p className="font-jakarta text-xl text-white/90 mb-8 tracking-tight antialiased">
              Let our experts guide you through every step of your study abroad
              adventure
            </p>
            <Button
              variant="primary"
              size="lg"
              rightIcon={<ArrowRight className="w-5 h-5" />}
              onClick={() =>
                openWhatsApp(
                  "Hello! I'm interested in getting started with BeginPack Ltd. Please assist me.\n\nName:\nEmail:\nPhone:\nService of interest:",
                )
              }
            >
              Start Your Journey Today
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
